import { DEFAULT_SUPPORT_LINKS } from "../support/constants";
import { CbhFeatureFlag } from "./CbhFeatureFlag";

export const FEATURE_FLAG_DEFAULT_VALUES = {
  [CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC]: false,
  [CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS]: false,
  [CbhFeatureFlag.ENABLE_COGNITO_PHONE_LOGIN]: false,
  [CbhFeatureFlag.ENABLE_COGNITO_MAGIC_LINK_LOGIN]: false,
  [CbhFeatureFlag.SENT_HOME_REQUEST_CONFIG]: {
    isSentHomeFlowV2Enabled: true,
    confirmationAlert: {
      header: "Are you sure?",
      message:
        "You are currently not at the facility. Falsely reporting the facility sent you home may negatively impact the status of your account. Please confirm you were sent home by the facility.",
    },
    failureToast: {
      header: "Oops, your request didn't go through",
      message: "Please try again or contact the facility directly.",
    },
    successToast: {
      header: "Your request has been sent to the facility",
      message:
        "Do not cancel this shift! It will remain in your calendar until the facility confirms your sent home request.",
    },
  },
  [CbhFeatureFlag.LICENSE_RECIPROCITY]: false,
  [CbhFeatureFlag.UNDO_BOOKING_TIME_THRESHOLD]: 300,
  [CbhFeatureFlag.UNDO_BOOKING_BUFFER_AFTER_BUTTON_CLICK_IN_SECONDS]: 15,
  [CbhFeatureFlag.UNDO_BOOKING_LEAD_TIME_CONFIGURATION]: {
    enabled: false,
    leadTimeInMinutes: 0,
  },
  [CbhFeatureFlag.HYPER_TRACK_GEOTAG_EVENTS]: {
    events: "failures",
  } as const,
  [CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT]: false,
  [CbhFeatureFlag.ZENDESK_HC_ARTICLE_SSO]: false,
  [CbhFeatureFlag.EDUCATE_FOR_FIRST_N_BOOKINGS]: 0,
  [CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION]: {
    limited: {
      enabled: false,
    },
    warning: {
      enabled: false,
    },
  },
  [CbhFeatureFlag.SEE_PAST_SHIFTS_AND_PAPER_TIMESHEET]: false,
  [CbhFeatureFlag.CANCELLATION_STATS]: false,
  [CbhFeatureFlag.HACKATHON_TIMECLOCK_DIRECTIONS]: false,
  [CbhFeatureFlag.DOCUMENT_EXCLUSION_LIST]: {
    ids: [],
  },
  [CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT]: {
    mergePdf: false,
    multiFile: false,
    multiPhoto: false,
  },
  [CbhFeatureFlag.ARRIVAL_TIME_CONFIGURATION]: {
    gracePeriodInMinutesForDriveETA: 30,
    preferredEtaProvider: "GOOGLE_MAPS" as const,
    shortLeadTimeOffsetInHours: 4,
    useExpectedArrivalTime: false,
  },
  [CbhFeatureFlag.SUPPORT_LINKS]: DEFAULT_SUPPORT_LINKS,
  [CbhFeatureFlag.FORTUNE_COOKIE_ANIMATION]: {
    enabled: false,
    fortuneMessages: ["HELLO", "HI"],
  },
  [CbhFeatureFlag.ARRIVAL_TIME_FLOW_WORKPLACE_EXCLUSIONS_CONFIG]: {
    workplaceExclusions: {
      msas: [],
      workplaceIds: [],
    },
  },
  [CbhFeatureFlag.CONTRACTOR_AGREEMENT_RE_SIGNING]: {
    enabled: false,
    cutoffTimestamp: "2100-01-01T00:00:00.000Z",
  },
  [CbhFeatureFlag.ENABLE_NEW_STRIPE_ONBOARDING]: false,
  [CbhFeatureFlag.ENABLE_STRIPE_RESTRICTION_BANNER]: false,
  [CbhFeatureFlag.ROLLOUT_DETECTED_CHARTING_CREDENTIALS]: false,
  [CbhFeatureFlag.ENABLE_VIDEO_POSTING]: false,
  [CbhFeatureFlag.FORM_1099_UI]: {
    enabled: false,
  },
  [CbhFeatureFlag.LIVE_ACTIVITIES_CONFIG]: {
    isFeatureEnabled: false,
    departureBufferInMinutes: 5,
    minimumDistanceMovedForLiveEtaViewInMeters: 100,
    minimumEtaDecreaseForLiveEtaViewInMinutes: 5,
    liveActivityTriggerLeadTimeMinimumInMinutes: 110,
    liveActivityTriggerLeadTimeMaximumInMinutes: 110,
    etaFreshnessThresholdInMinutes: 5,
    leaveSoonThresholdMinutes: 20,
    leaveNowWindowMinutes: 5,
    leaveInMinutesWarningThreshold: 5,
  },
  [CbhFeatureFlag.WORKER_ETA_IMPROVEMENTS]: {
    enabled: false,
    isClipboardScorePageChangesEnabled: false,
    stationaryMinutesThresholdForEtaNudge: 8,
    isLateThresholdMinutes: 5,
    isEtaNudgeEnabled: false,
    shiftLeadTimeToUpdateEtaMinutes: 120,
    maxMinutesAheadToUpdateEta: 120,
  },
  [CbhFeatureFlag.AUTO_CLOCKOUT_SHIFT_END_THRESHOLD]: {
    shiftEndThreshold: 2,
    shiftWithGeofenceEndThreshold: 6,
  },
  [CbhFeatureFlag.TIMECLOCK_INFO_WHEN_CONTACTING_SUPPORT]: false,
  [CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW]: {
    enabled: false,
    delayMinutes: 120,
    useDelayedReviewUI: false,
  },
  [CbhFeatureFlag.ATTENDANCE_TRACKING_METHODS_ROLLOUT]: {
    state: "off" as const,
    minimalOtaVersion: "*",
    minimalStoreVersion: "*",
    methodsConfiguration: undefined,
  },
  [CbhFeatureFlag.ENABLE_BADGE_VERIFICATION_CLOCK_IN]: false,
  [CbhFeatureFlag.WORKER_FORCED_CANCELLATION_CHAT_WARNING]: {
    workplaceIds: [],
  },
  [CbhFeatureFlag.EARLY_ACCESS_FACILITIES]: false,
  [CbhFeatureFlag.LOCATION_CHANGE_PROMPT_CONFIG]: {
    enabled: false,
    cooldownInHours: 72,
    distanceThresholdInMiles: 300,
  },
  [CbhFeatureFlag.WORKPLACE_REVIEW_UX_UPDATE]: {
    enabled: false,
    questionFiltersToBeShownWhenEnabled: [
      { tag: "PATIENTS_PER_NURSE", type: "NUMBER" },
      { tag: "SUPPLY_RATING", type: "RATING" },
      { tag: "CLEANLINESS", type: "RATING" },
      { tag: "CULTURE", type: "RATING" },
      { tag: "PATIENT_CARE", type: "RATING" },
    ],
    questionFiltersToBeHiddenWhenDisabled: [
      { tag: "SUPPLY_RATING", type: "RATING" },
      { tag: "CLEANLINESS", type: "RATING" },
      { tag: "CULTURE", type: "RATING" },
      { tag: "PATIENT_CARE", type: "RATING" },
    ],
  },
  [CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT]: {
    verifiedShiftsLookBackPeriodInDays: 30,
    offsetToShiftEndForWorkerRemoval: 4,
    allowedQualifications: ["CNA", "RN", "LVN"],
    channelUrlSuffix: "clipboard-guide",
    channelNamePrefix: "Ask Veterans from",
    experiencedWorkerLimit: 5,
    enabled: false,
    notifyExperiencedWorkers: false,
    recentMessageLimit: 5,
    llmClassificationEnabled: false,
  },
  [CbhFeatureFlag.WORKER_CHAT_V2]: false,
};
